import { FileText, Cpu, GraduationCap, ArrowRight, ArrowUpRight } from "lucide-react";

function VennGraphic() {
  return (
    <svg viewBox="0 0 240 160" className="h-auto w-full">
      {/* Backdrop dotted grid */}
      <defs>
        <pattern id="dot-grid" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.8" fill="#E5E5E5" />
        </pattern>
      </defs>
      <rect width="240" height="160" fill="url(#dot-grid)" rx="14" />
      {/* Circle 1 — Digital Systems */}
      <circle cx="92" cy="80" r="58" fill="#0A0A0A" fillOpacity="0.06" stroke="#0A0A0A" strokeOpacity="0.55" />
      {/* Circle 2 — Education */}
      <circle cx="156" cy="80" r="58" fill="#0A0A0A" fillOpacity="0.06" stroke="#0A0A0A" strokeOpacity="0.55" />
      {/* Overlap center mark */}
      <g>
        <rect x="116" y="68" width="24" height="24" rx="3" fill="#0A0A0A" />
        <text
          x="128"
          y="84"
          textAnchor="middle"
          fontFamily="'Source Code Pro', monospace"
          fontSize="11"
          fontWeight="700"
          fill="#FFFFFF"
        >
          {"<>"}
        </text>
      </g>
      {/* Labels */}
      <g fontFamily="'Source Code Pro', monospace" fontSize="9" fill="#737373">
        <text x="44" y="36" textAnchor="middle">DIGITAL SYSTEMS</text>
        <text x="200" y="36" textAnchor="middle">EDUCATION</text>
        <text x="128" y="138" textAnchor="middle" fill="#0A0A0A" fontWeight="700">
          CONTENTIUM TECH
        </text>
      </g>
    </svg>
  );
}

function FrictionGraphic() {
  return (
    <svg viewBox="0 0 240 160" className="h-auto w-full">
      <defs>
        <pattern id="dot-grid-2" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.8" fill="#E5E5E5" />
        </pattern>
        <marker
          id="arrow-head"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M0,0 L10,5 L0,10 z" fill="#0A0A0A" />
        </marker>
      </defs>
      <rect width="240" height="160" fill="url(#dot-grid-2)" rx="14" />
      {/* Friction side label */}
      <text x="18" y="22" fontFamily="'Source Code Pro', monospace" fontSize="9" fill="#737373">
        BEFORE
      </text>
      {/* Tangled lines */}
      <g stroke="#A3A3A3" strokeWidth="1.4" fill="none" strokeLinecap="round">
        <path d="M14 60 C 30 40, 50 90, 80 55 S 110 90, 130 70" />
        <path d="M14 90 C 40 75, 50 110, 80 95 S 100 70, 120 90" />
        <path d="M14 120 C 30 100, 60 130, 80 115 S 100 95, 120 110" />
      </g>
      {/* Vertical divider */}
      <line x1="138" y1="30" x2="138" y2="142" stroke="#D4D4D4" strokeDasharray="3 4" />
      {/* After side label */}
      <text x="226" y="22" fontFamily="'Source Code Pro', monospace" fontSize="9" fill="#737373" textAnchor="end">
        AFTER
      </text>
      {/* Clean straight arrow */}
      <line
        x1="150"
        y1="90"
        x2="222"
        y2="90"
        stroke="#0A0A0A"
        strokeWidth="2.5"
        markerEnd="url(#arrow-head)"
      />
      {/* Goal node */}
      <circle cx="228" cy="90" r="0" fill="#0A0A0A" />
      {/* Tiny step ticks */}
      <g fill="#737373">
        <circle cx="162" cy="90" r="1.6" />
        <circle cx="182" cy="90" r="1.6" />
        <circle cx="202" cy="90" r="1.6" />
      </g>
      {/* Labels */}
      <text x="76" y="148" fontFamily="'Source Code Pro', monospace" fontSize="9" fill="#737373" textAnchor="middle">
        FRICTION
      </text>
      <text x="186" y="148" fontFamily="'Source Code Pro', monospace" fontSize="9" fill="#0A0A0A" fontWeight="700" textAnchor="middle">
        ONE CLEAN PATH
      </text>
    </svg>
  );
}

function EduzyPreview() {
  return (
    <div
      data-testid="about-eduzy-preview"
      className="relative overflow-hidden rounded-3xl border border-neutral-200 bg-[#0A0A0A] p-8 text-neutral-100 md:p-10"
    >
      <div className="grid items-center gap-10 md:grid-cols-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
            What learners see
          </p>
          <h3 className="mt-3 font-display text-2xl font-semibold leading-tight text-white md:text-3xl">
            The system, made simple — for one learner at a time.
          </h3>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-400">
            Beneath the interface sits the knowledge graph, the AI pipeline, and
            the editorial care — composed into a single, calm experience.
          </p>
          <a
            href="https://eduzy.co"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-xs font-semibold text-[#0A0A0A] transition-colors hover:bg-neutral-200"
          >
            See Eduzy <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="relative h-[300px] md:h-[340px]" style={{ perspective: "1200px" }}>
          <div
            className="absolute inset-x-2 top-3 rounded-2xl border border-neutral-700/80 bg-[#171717] shadow-2xl"
            style={{ transform: "rotateY(-12deg) rotateX(6deg)", transformStyle: "preserve-3d" }}
          >
            <div className="flex items-center gap-2 border-b border-neutral-800 px-4 py-2.5">
              <span className="h-2 w-2 rounded-full bg-neutral-600" />
              <span className="h-2 w-2 rounded-full bg-neutral-600" />
              <span className="h-2 w-2 rounded-full bg-neutral-600" />
              <span className="ml-3 rounded-md bg-neutral-800 px-3 py-1 font-mono text-[10px] text-neutral-400">
                eduzy.co / lesson / fractions
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4 p-5">
              <div className="col-span-2 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="font-display text-base font-semibold text-white">
                    Fractions — Lesson 04
                  </p>
                  <span className="rounded-full bg-neutral-800 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-neutral-300">
                    Live
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-800">
                  <div className="h-full w-[62%] rounded-full bg-white" />
                </div>
                <div className="space-y-2 pt-2">
                  <div className="h-2 w-full rounded bg-neutral-800" />
                  <div className="h-2 w-11/12 rounded bg-neutral-800" />
                  <div className="h-2 w-3/4 rounded bg-neutral-800" />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-[11px] text-neutral-300">
                    A. ½ + ⅓
                  </div>
                  <div className="rounded-lg border border-white bg-white px-3 py-2 text-[11px] font-semibold text-[#0A0A0A]">
                    B. ⅔ + ¼ ✓
                  </div>
                  <div className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-[11px] text-neutral-300">
                    C. ¾ + ½
                  </div>
                  <div className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-[11px] text-neutral-300">
                    D. ⅖ + ¼
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                  Mastery
                </p>
                <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-3">
                  <div className="flex items-baseline gap-1">
                    <span className="font-display text-2xl font-bold text-white">78</span>
                    <span className="text-xs text-neutral-500">%</span>
                  </div>
                  <p className="mt-1 text-[10px] text-neutral-500">+6 this week</p>
                </div>
                <div className="space-y-1.5">
                  <div className="h-1 rounded bg-neutral-700" />
                  <div className="h-1 w-4/5 rounded bg-neutral-700" />
                  <div className="h-1 w-3/5 rounded bg-neutral-700" />
                  <div className="h-1 w-2/3 rounded bg-neutral-700" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="ct-container scroll-mt-28 py-24 md:py-32"
    >
      {/* STEP 1 — Heading + lead */}
      <div className="grid items-end gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className="ct-eyebrow">About Us</span>
          <h2 className="mt-4 font-display text-4xl font-bold leading-tight tracking-tight text-[#0F172A] sm:text-5xl">
            Knowledge,
            <br />
            built like <span className="text-[#737373]">a system.</span>
          </h2>
        </div>
        <div className="lg:col-span-7">
          <p className="font-display text-2xl leading-snug text-[#0F172A] md:text-3xl">
            Contentium Tech is a technology-driven content company built with a vision
            to scale knowledge and make learning accessible across boundaries.
          </p>
        </div>
      </div>

      {/* STEP 2 — How it works (pipeline) */}
      <div className="mt-14">
        <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
          How it works
        </p>
        <div
          data-testid="about-pipeline"
          className="relative grid grid-cols-1 gap-4 rounded-3xl border border-neutral-200 bg-white p-6 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-stretch md:gap-0 md:p-8"
        >
          <div className="flex flex-col items-start gap-3 px-4 py-4 md:py-2">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#0A0A0A] text-white">
              <FileText className="h-5 w-5" />
            </span>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
                Input
              </p>
              <h3 className="mt-1 font-display text-lg font-semibold text-[#0F172A]">
                Content
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                Raw curriculum, concepts, and references — gathered with editorial care.
              </p>
            </div>
          </div>
          <div className="hidden items-center justify-center md:flex">
            <ArrowRight className="h-5 w-5 text-neutral-300" />
          </div>
          <div className="flex flex-col items-start gap-3 border-y border-neutral-200 bg-[#FAFAFA] px-5 py-5 md:border-x md:border-y-0 md:py-2">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#0A0A0A] text-white">
              <Cpu className="h-5 w-5" />
            </span>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
                Engine
              </p>
              <h3 className="mt-1 font-display text-lg font-semibold text-[#0F172A]">
                AI Pipeline
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                Structuring, retrieving, and adapting knowledge with automation.
              </p>
            </div>
          </div>
          <div className="hidden items-center justify-center md:flex">
            <ArrowRight className="h-5 w-5 text-neutral-300" />
          </div>
          <div className="flex flex-col items-start gap-3 px-4 py-4 md:py-2">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#0A0A0A] text-white">
              <GraduationCap className="h-5 w-5" />
            </span>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
                Outcome
              </p>
              <h3 className="mt-1 font-display text-lg font-semibold text-[#0F172A]">
                Learner
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                Simple, accessible, and personalized learning at scale.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* STEP 3 — Why it matters (two reworked graphic cards) */}
      <div className="mt-14">
        <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
          Why it matters
        </p>
        <div className="grid gap-6 md:grid-cols-2">
          {/* Card 1 — Venn */}
          <article className="group flex flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white">
            <div className="bg-[#FAFAFA] p-6 md:p-8">
              <VennGraphic />
            </div>
            <div className="border-t border-neutral-200 p-7">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
                01 / Intersection
              </p>
              <h3 className="mt-2 font-display text-xl font-semibold text-[#0F172A]">
                Where digital ecosystems meet education
              </h3>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                Founded with a deep understanding of digital ecosystems, the company
                combines content, automation, and AI to create meaningful, scalable
                platforms that deliver real-world impact.
              </p>
            </div>
          </article>

          {/* Card 2 — Friction → Flow */}
          <article className="group flex flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white">
            <div className="bg-[#FAFAFA] p-6 md:p-8">
              <FrictionGraphic />
            </div>
            <div className="border-t border-neutral-200 p-7">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
                02 / Simplicity
              </p>
              <h3 className="mt-2 font-display text-xl font-semibold text-[#0F172A]">
                Removing friction from learning
              </h3>
              <p className="mt-3 text-base leading-relaxed text-slate-700">
                With a strong focus on simplicity and accessibility, we remove the
                friction between people and knowledge — across devices, classrooms,
                and digital platforms.
              </p>
            </div>
          </article>
        </div>
      </div>

      {/* STEP 4 — What it becomes (Eduzy preview) */}
      <div className="mt-14">
        <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-neutral-500">
          What it becomes
        </p>
        <EduzyPreview />
      </div>
    </section>
  );
}
